import { useCallback, useEffect, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useApplicationFeatureFlag } from "../../../infra/application/capabilities/hooks/useApplicationFeatureFlag";
import { FEATURE_FLAGS } from "../../../infra/constants/featureFlags";
import appLogger from "../../../infra/logger/appLogger";
import { useAppDispatch, useAppSelector } from "../../hook";
import useUserInfo from "../useUserInfo";
import normalizeLanguageCodes from "./normalizeLanguageCodes";
import { applyUrlLanguageFilter, setSearchByMyPreferredLanguage, setSelectedOriginalLanguages, updateUserPersist } from "./userPersistSlice";
const userPersistLogger = appLogger.withTag("UserPersist");
const PERSIST_DEBOUNCE_TIME = 2000;
const createEmptyPendingPersist = () => ({ ownerUserId: null, updates: {} });
/**
 * The language filter controls, and the write-back that persists them.
 *
 * Local state is the source of truth: every interaction updates the store
 * immediately and the server write follows, debounced. A rejected write is a
 * logged non-event, so the filter keeps working when the endpoint does not.
 */
export default function useUserPersist() {
    const dispatch = useAppDispatch();
    const { isAuthenticated, isAuthLoaded, onlyUserId } = useUserInfo();
    const { isFlagEnabled: isLanguageFilterEnabled, isFlagReady: isLanguageFilterFlagReady } = useApplicationFeatureFlag(FEATURE_FLAGS.FILTER_LANGUAGE);
    // `userPersist` is registered in `defaultReducers`, so the slice is always
    // present and these need no fallback.
    const selectedOriginalLanguages = useAppSelector((state) => state.userPersist.selected_original_languages);
    const isSearchByMyPreferredLanguageEnabled = useAppSelector((state) => state.userPersist.search_by_my_preferred_language);
    const isPersistStoreLoaded = useAppSelector((state) => state.userPersist.isLoaded);
    // `isAuthenticated` is false until the auth check resolves, so it only means
    // "guest" once auth has loaded. Without that guard this would report loaded
    // during the auth window and the feed would render, then change underneath
    // the user when the persisted filter arrived.
    const isGuest = isAuthLoaded && !isAuthenticated;
    // A guest has nothing to load, and neither does a user with the feature off.
    const isUserPersistLoaded = isGuest || (isLanguageFilterFlagReady && !isLanguageFilterEnabled) || isPersistStoreLoaded;
    const signedInUserId = isAuthenticated ? onlyUserId : null;
    const refPendingPersist = useRef(createEmptyPendingPersist());
    const persistLanguageFilter = useDebouncedCallback(() => {
        const { ownerUserId, updates } = refPendingPersist.current;
        refPendingPersist.current = createEmptyPendingPersist();
        // Changes are written only to the account that made them. A guest who
        // signs in, or a user who switches accounts, inside the debounce window
        // must not have those changes replayed onto the new account.
        if (!ownerUserId || ownerUserId !== signedInUserId || !isLanguageFilterEnabled) {
            return;
        }
        const payload = {};
        if (updates.selectedOriginalLanguages !== undefined) {
            payload.selected_original_languages = updates.selectedOriginalLanguages;
        }
        if (updates.searchByMyPreferredLanguage !== undefined) {
            payload.search_by_my_preferred_language = updates.searchByMyPreferredLanguage;
        }
        if (Object.keys(payload).length === 0) {
            return;
        }
        dispatch(updateUserPersist(payload))
            .unwrap()
            .catch((error) => {
            // A rejected write is a logged non-event: the filter still applies for
            // the session and nothing is surfaced to the user.
            userPersistLogger.warn("failed to persist language filter state", { error });
        });
    }, PERSIST_DEBOUNCE_TIME);
    useEffect(() => {
        return () => {
            persistLanguageFilter.flush();
        };
    }, [persistLanguageFilter]);
    const queuePersistUpdates = useCallback((updates) => {
        // A change from a different account (or a guest) starts a fresh queue
        // rather than merging into another owner's pending changes.
        if (refPendingPersist.current.ownerUserId !== signedInUserId) {
            refPendingPersist.current = { ownerUserId: signedInUserId, updates: {} };
        }
        Object.assign(refPendingPersist.current.updates, updates);
        persistLanguageFilter();
    }, [persistLanguageFilter, signedInUserId]);
    const restoreLanguageFilterFromUrl = useCallback((languageFilter) => {
        dispatch(applyUrlLanguageFilter({
            selected_original_languages: languageFilter.selectedOriginalLanguages,
            search_by_my_preferred_language: languageFilter.searchByMyPreferredLanguage,
        }));
    }, [dispatch]);
    const onUserSelectsOriginalLanguages = useCallback((codes) => {
        const normalizedCodes = normalizeLanguageCodes(codes);
        dispatch(setSelectedOriginalLanguages(normalizedCodes));
        queuePersistUpdates({ selectedOriginalLanguages: normalizedCodes });
    }, [dispatch, queuePersistUpdates]);
    const onUserTogglesSearchByMyPreferredLanguage = useCallback((isEnabled) => {
        dispatch(setSearchByMyPreferredLanguage(isEnabled));
        queuePersistUpdates({ searchByMyPreferredLanguage: isEnabled });
    }, [dispatch, queuePersistUpdates]);
    const onUserResetsLanguageFilter = useCallback(() => {
        dispatch(setSelectedOriginalLanguages([]));
        dispatch(setSearchByMyPreferredLanguage(false));
        queuePersistUpdates({ selectedOriginalLanguages: [], searchByMyPreferredLanguage: false });
    }, [dispatch, queuePersistUpdates]);
    return {
        isSearchByMyPreferredLanguageEnabled,
        isUserPersistLoaded,
        onUserResetsLanguageFilter,
        onUserSelectsOriginalLanguages,
        onUserTogglesSearchByMyPreferredLanguage,
        restoreLanguageFilterFromUrl,
        selectedOriginalLanguages,
    };
}
